import React, { useCallback, useMemo, useState } from 'react';
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Coins,
  Download,
  FileSpreadsheet,
  FileText,
  ShieldCheck,
  Users,
  Wallet,
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import AppLoader from '../../components/modules/AppLoader';
import ModuleAlert from '../../components/modules/ModuleAlert';
import ModulePageHeader from '../../components/modules/ModulePageHeader';
import apiService from '../../utils/apiService';
import { useFetchOnceByKey } from '../../hooks/useMountOnce';
import { getThemeColor } from '../../config/appConfig';
import '../../styles/module-shared.css';
import './client-report.css';

const THEME = getThemeColor('primary');
const PAGE_SIZE = 15;

const EMPTY_FILTERS = { search: '', kyc: 'all', deposit: 'all', from: '', to: '' };

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(
    Number(amount) || 0
  );

const formatNumber = (n) => new Intl.NumberFormat('en-US').format(Number(n) || 0);

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const pad2 = (n) => String(n).padStart(2, '0');
const stamp = (d = new Date()) =>
  `${d.getFullYear()}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}_${pad2(d.getHours())}${pad2(d.getMinutes())}`;

/** Filters -> API body. Dates are sent as calendar days (no UTC shift). */
const toRequest = (f) => ({
  searchTerm: f.search.trim(),
  kycStatus: f.kyc,
  deposit: f.deposit,
  fromDate: f.from ? `${f.from}T00:00:00` : null,
  toDate: f.to ? `${f.to}T00:00:00` : null,
});

const parseReport = (res) => {
  const d = res?.data?.result ?? res?.data ?? null;
  if (!d || typeof d !== 'object') return null;
  const pickKey = (o, a, b) => o?.[a] ?? o?.[b];
  const summary = pickKey(d, 'summary', 'Summary') || {};
  const rows = pickKey(d, 'rows', 'Rows');
  return {
    partnerName: pickKey(d, 'partnerName', 'PartnerName') || '',
    partnerCode: pickKey(d, 'partnerCode', 'PartnerCode') || '',
    generatedAt: pickKey(d, 'generatedAt', 'GeneratedAt') || new Date().toISOString(),
    filtersApplied: pickKey(d, 'filtersApplied', 'FiltersApplied') || 'None',
    summary: {
      totalClients: Number(pickKey(summary, 'totalClients', 'TotalClients')) || 0,
      kycVerified: Number(pickKey(summary, 'kycVerified', 'KycVerified')) || 0,
      kycPending: Number(pickKey(summary, 'kycPending', 'KycPending')) || 0,
      depositedClients: Number(pickKey(summary, 'depositedClients', 'DepositedClients')) || 0,
      totalCommission: Number(pickKey(summary, 'totalCommission', 'TotalCommission')) || 0,
    },
    rows: (Array.isArray(rows) ? rows : []).map((r, i) => ({
      id: i,
      clientCode: pickKey(r, 'clientCode', 'ClientCode') || '—',
      countryCode: pickKey(r, 'countryCode', 'CountryCode') || '—',
      tradingAccounts: pickKey(r, 'tradingAccounts', 'TradingAccounts') || '—',
      registrationDate: pickKey(r, 'registrationDate', 'RegistrationDate'),
      kycStatus: pickKey(r, 'kycStatus', 'KycStatus') || 'Pending',
      kycVerified: Boolean(pickKey(r, 'kycVerified', 'KycVerified')),
      deposited: pickKey(r, 'deposited', 'Deposited') === 'Yes' ? 'Yes' : 'No',
      totalCommission: Number(pickKey(r, 'totalCommission', 'TotalCommission')) || 0,
    })),
  };
};

const saveBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
};

/** Same rows and totals as the screen and the Excel file. */
const buildPdf = (report) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const generated = new Date(report.generatedAt);

  doc.setFillColor(139, 0, 0);
  doc.rect(0, 0, pageW, 64, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text('ABET GLOBAL - CLIENT REPORT', 40, 30);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text('Partner-linked clients (authorized data only)', 40, 48);

  doc.setTextColor(40, 40, 40);
  doc.setFontSize(10);
  const info = [
    ['Partner', `${report.partnerName}${report.partnerCode ? `  (${report.partnerCode})` : ''}`],
    ['Generated', generated.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })],
    ['Filters', report.filtersApplied],
    [
      'Summary',
      `${report.summary.totalClients} clients  |  ${report.summary.kycVerified} KYC verified  |  ` +
        `${report.summary.depositedClients} deposited  |  Commission ${formatCurrency(report.summary.totalCommission)}`,
    ],
  ];
  let y = 86;
  info.forEach(([label, value]) => {
    doc.setFont('helvetica', 'bold');
    doc.text(`${label}:`, 40, y);
    doc.setFont('helvetica', 'normal');
    doc.text(String(value), 110, y);
    y += 16;
  });

  autoTable(doc, {
    startY: y + 6,
    head: [['#', 'Client Code', 'Country', 'Trading Accounts', 'Registration Date', 'KYC Status', 'Deposit', 'Total Commission']],
    body: report.rows.map((r, i) => [
      i + 1,
      r.clientCode,
      r.countryCode,
      r.tradingAccounts,
      formatDate(r.registrationDate),
      r.kycStatus,
      r.deposited,
      formatCurrency(r.totalCommission),
    ]),
    foot: report.rows.length
      ? [['', '', '', '', '', '', 'Total', formatCurrency(report.summary.totalCommission)]]
      : undefined,
    styles: { fontSize: 9, cellPadding: 5, textColor: [40, 40, 40] },
    headStyles: { fillColor: [139, 0, 0], textColor: 255, fontStyle: 'bold' },
    footStyles: { fillColor: [253, 242, 242], textColor: [40, 40, 40], fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [253, 247, 247] },
    columnStyles: { 0: { cellWidth: 28 }, 7: { halign: 'right' } },
    margin: { left: 40, right: 40, bottom: 40 },
    didDrawPage: () => {
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text('Confidential - for the authorized partner only', 40, pageH - 20);
    },
  });

  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i += 1) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text(`Page ${i} of ${pages}`, pageW - 40, pageH - 20, { align: 'right' });
  }

  doc.save(`Client_Report_${stamp(generated)}.pdf`);
};

const SummaryTile = ({ tone, icon: Icon, label, value }) => (
  <div className={`module-card cl-kpi cl-kpi--${tone}`}>
    <span className="cl-kpi__icon">
      <Icon className="w-5 h-5" />
    </span>
    <div className="min-w-0">
      <p className="cl-kpi__label">{label}</p>
      <p className="cl-kpi__value">{value}</p>
    </div>
  </div>
);

const ClientReport = () => {
  const [draft, setDraft] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [version, setVersion] = useState(0);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState('');

  const fetchReport = useCallback(
    async ({ cancelled }) => {
      setLoading(true);
      setError('');
      try {
        const res = await apiService.getClientReport(toRequest(applied));
        if (cancelled()) return;
        const parsed = res?.success ? parseReport(res) : null;
        if (parsed) {
          setReport(parsed);
          setPage(1);
        } else {
          setReport(null);
          setError(res?.message || res?.error || 'Unable to load the client report.');
        }
      } catch (err) {
        if (!cancelled()) {
          setReport(null);
          setError(err?.message || 'Unable to load the client report.');
        }
      } finally {
        if (!cancelled()) setLoading(false);
      }
    },
    [applied]
  );

  useFetchOnceByKey(`client-report-${version}`, fetchReport, true);

  const rows = useMemo(() => report?.rows ?? [], [report]);
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = useMemo(() => rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [rows, page]);

  const dateInvalid = Boolean(draft.from && draft.to && draft.from > draft.to);

  const applyFilters = () => {
    if (dateInvalid) return;
    setApplied(draft);
    setVersion((v) => v + 1);
  };

  const resetFilters = () => {
    setDraft(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setVersion((v) => v + 1);
  };

  const downloadExcel = async () => {
    setBusy('excel');
    setError('');
    try {
      const blob = await apiService.downloadClientReportExcel(toRequest(applied));
      saveBlob(blob, `Client_Report_${stamp()}.xlsx`);
    } catch (err) {
      setError(err?.message || 'Excel download failed.');
    } finally {
      setBusy('');
    }
  };

  const downloadPdf = () => {
    if (!report) return;
    setBusy('pdf');
    setError('');
    try {
      buildPdf(report);
    } catch {
      setError('PDF download failed.');
    } finally {
      setBusy('');
    }
  };

  const canDownload = !loading && report && rows.length > 0;

  return (
    <div className="page cl-page">
      <div className="page-inner">
        <div className="cl-head">
          <ModulePageHeader
            icon={Users}
            title="Client Report"
            subtitle="Report of your partner-linked clients"
            iconBg={`linear-gradient(135deg,${THEME},#a52a2a)`}
          />
          <div className="cl-downloads">
            <button type="button" className="cl-btn cl-btn--outline" onClick={downloadExcel} disabled={!canDownload || busy !== ''}>
              <FileSpreadsheet className="w-4 h-4" />
              {busy === 'excel' ? 'Preparing…' : 'Download Excel'}
            </button>
            <button type="button" className="cl-btn cl-btn--primary" onClick={downloadPdf} disabled={!canDownload || busy !== ''}>
              <FileText className="w-4 h-4" />
              {busy === 'pdf' ? 'Preparing…' : 'Download PDF'}
            </button>
          </div>
        </div>

        <div className="cl-note">
          <ShieldCheck className="w-4 h-4 shrink-0" />
          <span>
            Only your own clients are included, and only permitted fields. Names, emails, phone numbers and documents are never part of
            this report.
          </span>
        </div>

        <ModuleAlert message={error} />

        <section className="module-card cl-filters">
          <div className="cl-filters__grid">
            <label className="cl-field cl-field--wide">
              <span>Search</span>
              <input
                type="text"
                placeholder="Client code…"
                value={draft.search}
                onChange={(e) => setDraft((d) => ({ ...d, search: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
              />
            </label>
            <label className="cl-field">
              <span>KYC status</span>
              <select value={draft.kyc} onChange={(e) => setDraft((d) => ({ ...d, kyc: e.target.value }))}>
                <option value="all">All</option>
                <option value="verified">Verified</option>
                <option value="pending">Pending</option>
              </select>
            </label>
            <label className="cl-field">
              <span>Deposit</span>
              <select value={draft.deposit} onChange={(e) => setDraft((d) => ({ ...d, deposit: e.target.value }))}>
                <option value="all">All</option>
                <option value="yes">Deposited</option>
                <option value="no">Not deposited</option>
              </select>
            </label>
            <label className="cl-field">
              <span>Registered from</span>
              <input type="date" value={draft.from} max={draft.to || undefined} onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))} />
            </label>
            <label className="cl-field">
              <span>Registered to</span>
              <input type="date" value={draft.to} min={draft.from || undefined} onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))} />
            </label>
          </div>
          <div className="cl-filters__actions">
            {dateInvalid ? <span className="cl-filters__warn">"From" date must be before "To" date.</span> : <span />}
            <div className="flex gap-2">
              <button type="button" className="cl-btn cl-btn--ghost" onClick={resetFilters}>
                Reset
              </button>
              <button type="button" className="cl-btn cl-btn--primary" onClick={applyFilters} disabled={dateInvalid || loading}>
                Apply filters
              </button>
            </div>
          </div>
        </section>

        {loading ? (
          <div className="module-loader">
            <AppLoader />
          </div>
        ) : report ? (
          <>
            <section className="cl-kpis">
              <SummaryTile tone="blue" icon={Users} label="Total clients" value={formatNumber(report.summary.totalClients)} />
              <SummaryTile tone="green" icon={CheckCircle2} label="KYC verified" value={formatNumber(report.summary.kycVerified)} />
              <SummaryTile tone="amber" icon={Clock} label="KYC pending" value={formatNumber(report.summary.kycPending)} />
              <SummaryTile tone="violet" icon={Wallet} label="Deposited clients" value={formatNumber(report.summary.depositedClients)} />
              <SummaryTile tone="brand" icon={Coins} label="Total commission" value={formatCurrency(report.summary.totalCommission)} />
            </section>

            <section className="module-card cl-table-card">
              <div className="cl-table-head">
                <div>
                  <h2>Clients</h2>
                  <p>
                    {formatNumber(rows.length)} record{rows.length === 1 ? '' : 's'} · Filters: {report.filtersApplied}
                  </p>
                </div>
                <span className="cl-table-head__hint">
                  <Download className="w-3.5 h-3.5" /> Excel and PDF contain exactly these rows
                </span>
              </div>

              {rows.length === 0 ? (
                <div className="cl-empty">No clients match the selected filters.</div>
              ) : (
                <>
                  <div className="cl-table-wrap">
                    <table className="cl-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Client Code</th>
                          <th>Country</th>
                          <th>Trading Accounts</th>
                          <th>Registration Date</th>
                          <th>KYC</th>
                          <th>Deposit</th>
                          <th className="text-right">Total Commission</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pageRows.map((r, i) => (
                          <tr key={r.id}>
                            <td className="cl-muted">{(page - 1) * PAGE_SIZE + i + 1}</td>
                            <td className="cl-strong">{r.clientCode}</td>
                            <td>{r.countryCode}</td>
                            <td className="cl-accounts">{r.tradingAccounts}</td>
                            <td>{formatDate(r.registrationDate)}</td>
                            <td>
                              <span className={`cl-pill ${r.kycVerified ? 'is-ok' : 'is-warn'}`}>{r.kycStatus}</span>
                            </td>
                            <td>
                              <span className={`cl-pill ${r.deposited === 'Yes' ? 'is-ok' : 'is-off'}`}>{r.deposited}</span>
                            </td>
                            <td className="text-right cl-strong">{formatCurrency(r.totalCommission)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="cl-pager">
                    <span>
                      Page {page} of {totalPages}
                    </span>
                    <div className="flex gap-2">
                      <button type="button" className="cl-btn cl-btn--ghost cl-btn--icon" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} aria-label="Previous page">
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <button type="button" className="cl-btn cl-btn--ghost cl-btn--icon" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} aria-label="Next page">
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </>
              )}
            </section>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default ClientReport;
